import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Container } from "reactstrap";
import {
  useDeleteMenuMutation,
  useGetMenusListQuery,
  useUpdateMenuMutation,
} from "../../../ReduxToolkit/api/menus";
import { useAppDispatch } from "../../../ReduxToolkit/Hooks";
import {
  closeModal,
  openModal,
} from "../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import { useModalSelector } from "../../../hooks/useModalSelector";
import ConfirmDeleteDialog from "../../../CommonElements/ConfirmDeleteDialog";
import { showApiResultAlert } from "../../../utils/AlertManager/showApiAlert";
import MenuFormModal from "./modal/MenuFormModal";
import MenusListTable from "./table/MenusListTable";

const MenusContainer = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isOpen, modalType, selectedId } = useModalSelector();
  const [selectedMenu, setSelectedMenu] = useState(null);
  const { isFetching, isLoading, data } = useGetMenusListQuery(undefined);
  const [deleteMenu, { isLoading: isDeleting, reset: resetDeleteMenu }] =
    useDeleteMenuMutation();
  const [updateMenuStatus, { reset: resetUpdateMenuStatus }] =
    useUpdateMenuMutation();
  const menus = data?.data ?? [];

  const handleAddMenu = () => {
    setSelectedMenu(null);
    dispatch(openModal({ modalType: "menuFormModal" }));
  };

  const handleAddUserMenu = () => {
    navigate("/menus/assign-user-menu");
  };

  const handleEditMenu = (menu) => {
    setSelectedMenu(menu);
    dispatch(openModal({ modalType: "menuFormModal" }));
  };

  const handleOpenDeleteDialog = (menu) => {
    setSelectedMenu(menu);
    dispatch(openModal({ modalType: "menuDeleteDialog", selectedId: menu.id }));
  };

  const handleResetDeleteMenu = () => {
    resetDeleteMenu();
    setSelectedMenu(null);
    dispatch(closeModal());
  };

  const handleDeleteMenu = async () => {
    if (!selectedId) return;

    const response = await deleteMenu(Number(selectedId));

    showApiResultAlert(
      response,
      {
        title: "Delete Menu",
        source: "menuDeleteDialog",
      },
      handleResetDeleteMenu
    );
  };

  const handleToggleMenuStatus = async (menu, value) => {
    const response = await updateMenuStatus({
      id: menu.id,
      data: {
        name: menu.name,
        route: menu.route,
        icon: menu.icon,
        parent_id: menu.parent_id,
        order: menu.order,
        sort_order: menu.sort_order,
        is_default: Boolean(menu.is_default),
        status: value ? 1 : 0,
      },
    });

    showApiResultAlert(
      response,
      {
        title: value ? "Menu Activated" : "Menu Deactivated",
        source: "menuStatusToggle",
      },
      () => resetUpdateMenuStatus()
    );
  };

  return (
    <Container fluid>
      <Card className="mt-4">
        <CardBody>
          <MenusListTable
            list={menus}
            loading={isLoading || isFetching}
            onAdd={handleAddMenu}
            onAddUserMenu={handleAddUserMenu}
            onEdit={handleEditMenu}
            onDelete={handleOpenDeleteDialog}
            onStatusToggle={handleToggleMenuStatus}
          />
        </CardBody>
        {isOpen && modalType === "menuFormModal" && (
          <MenuFormModal menus={menus} selectedMenu={selectedMenu} />
        )}
        {isOpen && modalType === "menuDeleteDialog" && (
          <ConfirmDeleteDialog
            isOpen={isOpen}
            size="sm"
            label={`Are you sure you want to delete ${
              selectedMenu?.name ?? "this menu"
            }?`}
            loading={isDeleting}
            loadingText="Deleting menu..."
            handleToggle={() => dispatch(closeModal())}
            handleClickFirstBtn={handleDeleteMenu}
          />
        )}
      </Card>
    </Container>
  );
};

export default MenusContainer;
